import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { wrapPromise } from "go-core";
import SubNavigationItems, { SubNavigationItemProps } from "go-app/components/Navigation/SubNavigationItems";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentContext } from "go-segment/context";
import { SegmentApi } from "go-segment/services/types";
import { useHasInvoicePermission } from "../../hooks/useHasInvoicePermission";
import { CurrentDocumentType } from "../../services/types";
import {
	bundleListName,
	bundleResourceType,
	invoiceListName,
	invoiceResourceType,
	orderListName,
	orderResourceType,
	purchaseListName,
	purchaseResourceType,
	stockAdjustmentListName,
	stockAdjustmentResourceType,
	transferListName,
	transferResourceType,
} from "../../utils";
import DocumentsNavigationSegments from "./DocumentsNavigationSegments";
import { findDocumentActiveSegment, getDocumentActiveSegmentSlug } from "./utils";

const DocumentsNavigation = () => {
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const prefix = `/${organization.id}/documents`;
	const location = useLocation();
	const [currentDocumentType, setCurrentDocumentType] = useState<CurrentDocumentType | undefined>();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const [selectedSegment, setSelectedSegment] = useState<SegmentApi | undefined>(
		findDocumentActiveSegment(resource?.read()?.segments || [], location)
	);
	const history = useHistory();
	const dispatch = useDispatch();
	const { hasInvoiceBasicPermission } = useHasInvoicePermission();

	useEffect(() => {
		handleChangeCurrentDocumentType();
	}, [location.pathname]);

	useEffect(() => {
		fetchSegments();
	}, [currentDocumentType]);

	const handleChangeCurrentDocumentType = () => {
		if (location.pathname.includes("stock_adjustments")) {
			setCurrentDocumentType("stock_adjustment");
		} else if (location.pathname.includes("transfers")) {
			setCurrentDocumentType("transfer");
		} else if (location.pathname.includes("purchases")) {
			setCurrentDocumentType("purchase");
		} else if (location.pathname.includes("sales")) {
			setCurrentDocumentType("order");
		} else if (location.pathname.includes("bundles")) {
			setCurrentDocumentType("bundle");
		} else if (location.pathname.includes("invoices")) {
			setCurrentDocumentType("invoice");
		} else {
			setCurrentDocumentType(undefined);
		}
	};

	const handleChangeSegment = (segment: SegmentApi | undefined, slug?: string) => {
		if (slug) {
			setSelectedSegment(undefined);
		} else {
			setSelectedSegment(segment);
		}

		const documentTypePath = `${currentDocumentType === "order" ? "sales" : `${currentDocumentType}s`}`;
		if (!location.pathname.endsWith(documentTypePath)) {
			const segmentSlug = slug || segment?.slug || undefined;
			dispatch({ type: "setSegment", segment: segmentSlug });
			history.push({
				pathname: `/${organization.id}/documents/${documentTypePath}`,
				search: segmentSlug ? `s=${segmentSlug}` : "",
			});
		} else {
			const searchParts = location.search.split("&");
			if (searchParts[0].startsWith("?s=")) {
				const segmentSlug = slug || segment?.slug || "";
				searchParts[0] = `?s=${segmentSlug}`;
			}
			dispatch({ type: "setSegment", segment: slug ?? getDocumentActiveSegmentSlug(location) });
			history.replace({ ...location, search: searchParts.join("&") });
		}
	};

	const handleOnClickItem = (event: React.MouseEvent<HTMLElement, MouseEvent>, documentType: CurrentDocumentType) => {
		if ((event.target as Element).baseURI.includes(documentType === "order" ? "sales" : documentType)) {
			event.preventDefault();
			handleChangeSegment(undefined, getSelectedSegmentForListConfig(resource?.read()?.segments || [], "all"));
		}
	};

	const items: SubNavigationItemProps[] = [
		{
			title: t("modules.purchase.header.title"),
			route: `${prefix}/purchases`,
			children:
				currentDocumentType === "purchase" ? (
					<DocumentsNavigationSegments
						segments={resource?.read()?.segments || []}
						handleChangeSegment={handleChangeSegment}
						selectedSegment={selectedSegment}
						setSelectedSegment={setSelectedSegment}
					/>
				) : undefined,
			onClick: (event) => handleOnClickItem(event, "purchase"),
		},
		{
			title: t("modules.order.header.title"),
			route: `${prefix}/sales`,
			children:
				currentDocumentType === "order" ? (
					<DocumentsNavigationSegments
						segments={resource?.read()?.segments || []}
						handleChangeSegment={handleChangeSegment}
						selectedSegment={selectedSegment}
						setSelectedSegment={setSelectedSegment}
					/>
				) : undefined,
			onClick: (event) => handleOnClickItem(event, "order"),
		},
		{
			title: t("modules.stock_adjustment.header.title"),
			route: `${prefix}/stock_adjustments`,
			children:
				currentDocumentType === "stock_adjustment" ? (
					<DocumentsNavigationSegments
						segments={resource?.read()?.segments || []}
						handleChangeSegment={handleChangeSegment}
						selectedSegment={selectedSegment}
						setSelectedSegment={setSelectedSegment}
					/>
				) : undefined,
			onClick: (event) => handleOnClickItem(event, "stock_adjustment"),
		},
		{
			title: t("modules.transfer.header.title"),
			route: `${prefix}/transfers`,
			children:
				currentDocumentType === "transfer" ? (
					<DocumentsNavigationSegments
						segments={resource?.read()?.segments || []}
						handleChangeSegment={handleChangeSegment}
						selectedSegment={selectedSegment}
						setSelectedSegment={setSelectedSegment}
					/>
				) : undefined,
			onClick: (event) => handleOnClickItem(event, "transfer"),
			hide: organization.more.stock_count <= 1,
		},
		{
			title: t("modules.bundle.header.title"),
			route: `${prefix}/bundles`,
			children:
				currentDocumentType === "bundle" ? (
					<DocumentsNavigationSegments
						segments={resource?.read()?.segments || []}
						handleChangeSegment={handleChangeSegment}
						selectedSegment={selectedSegment}
						setSelectedSegment={setSelectedSegment}
					/>
				) : undefined,
			onClick: (event) => handleOnClickItem(event, "bundle"),
		},
		{
			title: t("modules.invoice.header.title"),
			route: `${prefix}/invoices`,
			children:
				currentDocumentType === "invoice" ? (
					<DocumentsNavigationSegments
						segments={resource?.read()?.segments || []}
						handleChangeSegment={handleChangeSegment}
						selectedSegment={selectedSegment}
						setSelectedSegment={setSelectedSegment}
					/>
				) : undefined,
			onClick: (event) => handleOnClickItem(event, "invoice"),
			hide: !hasInvoiceBasicPermission,
		},
	];

	const getResourceType = () => {
		switch (currentDocumentType) {
			case "order":
				return orderResourceType;
			case "purchase":
				return purchaseResourceType;
			case "stock_adjustment":
				return stockAdjustmentResourceType;
			case "transfer":
				return transferResourceType;
			case "bundle":
				return bundleResourceType;
			case "invoice":
				return invoiceResourceType;
			default:
				return "";
		}
	};

	const getListName = () => {
		switch (currentDocumentType) {
			case "order":
				return orderListName;
			case "purchase":
				return purchaseListName;
			case "stock_adjustment":
				return stockAdjustmentListName;
			case "transfer":
				return transferListName;
			case "bundle":
				return bundleListName;
			case "invoice":
				return invoiceListName;
			default:
				return "";
		}
	};

	const fetchSegments = async () => {
		if (!getResourceType()) {
			setResource(undefined);
		} else {
			setResource(
				wrapPromise(
					segmentContextValue.get(getListName(), getResourceType(), [
						{
							type: getResourceType(),
							name: t(`enums.custom_fields.resources.${getResourceType()}`),
						},
					])
				)
			);
		}
	};

	return <SubNavigationItems title={t("components.navigation.documents.title")} items={items} />;
};

export default DocumentsNavigation;
